import {
  getData,
  updateData,
  // Used for creating with auto-generated keys
  removeData,
  subscribeToData,
  create // For creating with auto-generated keys like original app
} from './api-wrapper.service';
import { BeltPromotion, BeltConfig } from '../../types/belt';
// Notification creation is handled server-side by 'beltPromotionsChanged' Cloud Function
import logger from '../../utils/logger';
import {
  normalizeMartialArt,
  normalizeMartialArtSafe,
  parseStripeCount,
  buildBeltConfig,
  findOrCreateMartialArtId,
  normalizePhoto,
  getAcademy,
  getInstructor,
} from '../../utils/belt-helpers';


/**
 * Belt progression service for handling belt promotions and history
 * This service provides Firebase integration for the belt progression feature
 */

/**
 * Input type for the unified saveBeltPromotion function
 */
export interface SaveBeltPromotionInput {
  id?: string;              // If provided = update, else = create
  userId: string;
  date: number;
  beltColor: string;
  stripes?: number;
  martialArt: string;
  academy?: string;
  instructor?: string;
  notes?: string;
  photoURL?: string;
  beltConfig?: Partial<BeltConfig>;
  location?: {
    latitude?: number;
    longitude?: number;
    placeID?: string;
    locality?: string;
  } | null;
}

/**
 * Result type for the unified saveBeltPromotion function
 */
export interface SaveBeltPromotionResult {
  success: boolean;
  promotionId?: string;
  promotion?: BeltPromotion;
  errors?: string[];
}

/**
 * Unified save function for belt promotions with all D1-D8 fixes built in.
 * Creates a new promotion if no ID provided, updates existing if ID provided.
 *
 * D1: Validates and normalizes martial art name
 * D2: Parses and bounds-checks stripe count
 * D3: Handles notes field naming (notes → note for Firebase)
 * D4: Accepts and passes through location data
 * D5: Completes beltConfig with all required fields
 * D6: Uses deterministic martial art ID in profile sync (via updateUserBeltStatus)
 * D7: Normalizes photo data (string → object)
 * D8: Handles optional academy/instructor fields safely
 *
 * @param input Belt promotion data to save
 * @returns Result with success status, promotion ID, and any errors
 */
export const saveBeltPromotion = async (input: SaveBeltPromotionInput): Promise<SaveBeltPromotionResult> => {
  const errors: string[] = [];

  try {
    // D1: Validate and normalize martial art
    let martialArt: string;
    try {
      martialArt = normalizeMartialArt(input.martialArt);
    } catch (e) {
      errors.push('Martial art is required');
      return { success: false, errors };
    }

    // D2: Parse and validate stripes
    const stripes = parseStripeCount(input.stripes, 0);

    // D8: Handle optional fields safely
    const academy = getAcademy(input.academy);
    const instructor = getInstructor(input.instructor);

    // D3: Handle notes field
    const notes = input.notes ?? '';

    // D5: Build complete belt config
    const beltConfig = buildBeltConfig(input.beltColor, stripes, input.beltConfig);

    // D4: Pass through location (including null)
    const location = input.location ?? null;

    // D7: Normalize photo if provided
    const photoData = input.photoURL
      ? normalizePhoto({ photoURL: input.photoURL })
      : null;

    // Determine if this is create or update
    const isUpdate = !!input.id;

    if (isUpdate) {
      // Update existing promotion
      const updatedPromotion = await updateBeltPromotion(input.id!, {
        userId: input.userId,
        date: input.date,
        beltColor: input.beltColor,
        stripes,
        martialArt,
        academy,
        instructor,
        notes,
        photoURL: photoData?.url,
        beltConfig,
        // D4: Pass location through for updates
        location,
      });

      return {
        success: true,
        promotionId: input.id,
        promotion: updatedPromotion,
      };
    } else {
      // Create new promotion
      const newPromotion = await addBeltPromotion({
        userId: input.userId,
        date: input.date,
        beltColor: input.beltColor,
        stripes,
        martialArt,
        academy,
        instructor,
        notes,
        photoURL: photoData?.url,
        beltConfig,
        isActive: true,
        // D4: Pass location through for new promotions
        location,
      });

      return {
        success: true,
        promotionId: newPromotion.id,
        promotion: newPromotion,
      };
    }
  } catch (error: any) {
    logger.error('[saveBeltPromotion] Error saving promotion:', error);
    errors.push(error.message || 'Failed to save belt promotion');
    return { success: false, errors };
  }
};

/**
 * Normalize a belt color/name to its base color word so records migrated from
 * different legacy shapes ("Purple Belt" vs "purple") compare equal.
 */
const normalizeBeltColor = (raw: unknown): string => {
  const value = String(raw ?? '').toLowerCase();
  const match = value.match(/(white|blue|purple|brown|black|yellow|orange|green|red|coral)/);
  return match ? match[1] : value;
};

const parseStripes = (raw: unknown): number => {
  if (typeof raw === 'number') return raw;
  const parsed = parseInt(String(raw), 10);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Migrate every entry of a legacy profile's belt.promotionHistory array into
 * /beltPromotions/, skipping entries already represented there.
 *
 * Earlier migrations only copied the CURRENT belt, which collapsed a legacy
 * user's full history down to one record — and once any promotion existed,
 * the remaining history was never migrated. Running this on every read
 * repairs those accounts: after the first pass every item is represented and
 * no further writes happen.
 *
 * Migrated records are created with shownOnFeeds: false so historical
 * promotions never fan out to friends' feeds as if they were new.
 */
const migrateProfilePromotionHistory = async (
  userId: string,
  userProfile: any,
  existingPromotions: BeltPromotion[]
): Promise<BeltPromotion[]> => {
  const history = userProfile?.belt?.promotionHistory;
  if (!Array.isArray(history) || history.length === 0) return [];

  // An item is represented if a promotion shares its exact date, or the same
  // belt color + stripe count (a BJJ progression never repeats that pair).
  const isAlreadyMigrated = (item: any): boolean =>
    existingPromotions.some(p => {
      if (item.date && p.date === item.date) return true;
      const itemColor = normalizeBeltColor(item.belt);
      return !!itemColor &&
        normalizeBeltColor(p.beltColor) === itemColor &&
        (p.stripes || 0) === parseStripes(item.stripes);
    });

  const migrated: BeltPromotion[] = [];
  for (const item of history) {
    if (!item || isAlreadyMigrated(item)) continue;
    try {
      const promotion = await addBeltPromotion({
        userId,
        date: item.date || Date.now(),
        beltColor: normalizeBeltColor(item.belt) || 'white',
        stripes: parseStripes(item.stripes),
        martialArt: item.martialArt || 'Brazilian Jiu Jitsu',
        academy: item.academy,
        instructor: item.instructor,
        notes: item.notes,
        isActive: true,
        shownOnFeeds: false,
      });
      migrated.push(promotion);
      logger.info('[BeltProgressionService] Migrated promotionHistory item to /beltPromotions/:', promotion.id);
    } catch (migrationError) {
      logger.error('[BeltProgressionService] Failed to migrate promotionHistory item:', migrationError);
    }
  }
  return migrated;
};

/**
 * Get all belt promotions for a user
 * @param userId User ID to get promotions for
 * @returns Array of belt promotions
 */
export const getUserBeltPromotions = async (userId: string): Promise<BeltPromotion[]> => {
  try {
    // First check for BJJ-specific data in the user profile
    const userProfile = await getData(`/users/${userId}`);
    let hasBJJBeltInfo = false;
    
    if (userProfile?.belt && userProfile.belt["Brazilian Jiu Jitsu"]) {
      hasBJJBeltInfo = true;
      logger.info('[BeltProgressionService] Found BJJ belt in user profile:', userProfile.belt["Brazilian Jiu Jitsu"]);
    }
    
    // Get promotions from /beltPromotions/{userId}
    const promotionsData = await getData(`/beltPromotions/${userId}`);
    logger.info('[BeltProgressionService] Raw promotions data from Firebase:', promotionsData);
    
    // Main promotions array we'll build and return
    let promotions: BeltPromotion[] = [];

    // If there's no promotion data but user has belt info, migrate it to /beltPromotions/
    if (!promotionsData) {
      // Migrate the FULL legacy promotionHistory first (if any), so a legacy
      // account's history is never collapsed to just the current belt
      const migratedPromotions = userProfile
        ? await migrateProfilePromotionHistory(userId, userProfile, [])
        : [];

      // Then make sure the CURRENT belt is represented too (create a REAL promotion, not synthetic)
      if (hasBJJBeltInfo) {
        const bjjBelt = userProfile!.belt!["Brazilian Jiu Jitsu"];

        // Parse the stripe count carefully - check stripeCount (new) and stripes (legacy)
        let stripeCount = 0;
        if (bjjBelt.stripeCount !== undefined) {
          stripeCount = typeof bjjBelt.stripeCount === 'number' ? bjjBelt.stripeCount : parseInt(bjjBelt.stripeCount, 10) || 0;
        } else if (bjjBelt.stripes !== undefined) {
          stripeCount = typeof bjjBelt.stripes === 'number' ? bjjBelt.stripes : parseInt(bjjBelt.stripes, 10) || 0;
        } else if (userProfile?.stripesCount !== undefined) {
          stripeCount = typeof userProfile.stripesCount === 'number' ? userProfile.stripesCount : parseInt(userProfile.stripesCount, 10) || 0;
        }

        // Extract belt color robustly - check all possible field locations
        let beltColor = 'white';
        if (bjjBelt.baseColor) {
          beltColor = bjjBelt.baseColor;
        } else if (bjjBelt.designation) {
          const colorMatch = bjjBelt.designation.toLowerCase().match(/(white|blue|purple|brown|black|yellow|orange|green|red|coral)/);
          beltColor = colorMatch ? colorMatch[1] : bjjBelt.designation;
        } else if (bjjBelt.name) {
          const colorMatch = bjjBelt.name.toLowerCase().match(/(white|blue|purple|brown|black|yellow|orange|green|red|coral)/);
          beltColor = colorMatch ? colorMatch[1] : bjjBelt.name;
        } else if (bjjBelt.color) {
          beltColor = bjjBelt.color;
        }

        // Skip if the migrated history already contains the current belt
        const currentBeltRepresented = migratedPromotions.some(p =>
          (bjjBelt.dateObtained && p.date === bjjBelt.dateObtained) ||
          (normalizeBeltColor(p.beltColor) === normalizeBeltColor(beltColor) && (p.stripes || 0) === stripeCount)
        );

        if (!currentBeltRepresented) {
          logger.info('[BeltProgressionService] Migrating profile belt data to /beltPromotions/ for user:', userId);
          try {
            const migratedPromotion = await addBeltPromotion({
              userId,
              date: bjjBelt.dateObtained || Date.now(),
              beltColor,
              stripes: stripeCount,
              martialArt: 'Brazilian Jiu Jitsu',
              academy: userProfile.school?.name,
              instructor: userProfile.school?.instructor,
              isActive: true,
              // Migration of existing data, not a new promotion — keep it off feeds
              shownOnFeeds: false
            });
            migratedPromotions.push(migratedPromotion);
            logger.info('[BeltProgressionService] Successfully migrated belt data, new promotion ID:', migratedPromotion.id);
          } catch (migrationError) {
            logger.error('[BeltProgressionService] Failed to migrate belt data:', migrationError);
            // If migration fails, fall through with whatever history migrated
          }
        }
      }

      return migratedPromotions;
    }
    
    // Convert object to array and add IDs
    promotions = Object.entries(promotionsData).map(([id, data]: [string, any]) => {
      
      // Check if this is a BJJ belt (either explicitly or implicitly)
      // Handle both martialArt and martialArtName (original app field)
      const martialArtField = data.martialArtName || data.martialArt;
      const isBJJBelt =
        martialArtField?.toLowerCase().includes('jiu jitsu') ||
        martialArtField?.toLowerCase() === 'bjj' ||
        (!martialArtField || martialArtField === 'bjj'); // Default or old format
      
      // Parse the stripe count carefully
      let stripeCount = 0;
      
      // First try to get stripes from promotion data - check beltConfig.stripeCount or stipeCount
      // NOTE: Firebase rules use 'stipeCount' (original app typo), so both spellings must be checked
      const rawStripeCount = data.beltConfig?.stripeCount ?? data.beltConfig?.stipeCount;
      if (rawStripeCount !== undefined) {
        if (typeof rawStripeCount === 'number') {
          stripeCount = rawStripeCount;
        } else if (typeof rawStripeCount === 'string') {
          stripeCount = parseInt(rawStripeCount, 10);
          if (isNaN(stripeCount)) stripeCount = 0;
        }
      }
      // Fallback to direct stripes field
      else if (data.stripes !== undefined) {
        if (typeof data.stripes === 'number') {
          stripeCount = data.stripes;
        } else if (typeof data.stripes === 'string') {
          stripeCount = parseInt(data.stripes, 10);
          if (isNaN(stripeCount)) stripeCount = 0;
        }
      } 
      // For BJJ belts, try to get stripes from user profile if not in promotion
      else if (isBJJBelt) {
        // Try BJJ-specific data first
        if (userProfile?.belt?.["Brazilian Jiu Jitsu"]?.stripes !== undefined) {
          const bjjStripes = userProfile.belt["Brazilian Jiu Jitsu"].stripes;
          if (typeof bjjStripes === 'number') {
            stripeCount = bjjStripes;
          } else if (typeof bjjStripes === 'string') {
            stripeCount = parseInt(bjjStripes, 10);
            if (isNaN(stripeCount)) stripeCount = 0;
          }
        } 
        // Fallback to user profile stripesCount
        else if (userProfile?.stripesCount !== undefined) {
          const profileStripes = userProfile.stripesCount;
          if (typeof profileStripes === 'number') {
            stripeCount = profileStripes;
          } else if (typeof profileStripes === 'string') {
            stripeCount = parseInt(profileStripes, 10);
            if (isNaN(stripeCount)) stripeCount = 0;
          }
        }
      }
      
      // Create proper martial art name - handle both martialArt and martialArtName (original app field)
      let martialArt = data.martialArtName || data.martialArt || 'bjj';
      // Normalize BJJ names
      if (martialArt.toLowerCase().includes('jiu jitsu') || martialArt.toLowerCase() === 'bjj') {
        martialArt = 'Brazilian Jiu Jitsu';
      }
      
      // Extract belt color from beltConfig (original Marune structure)
      let beltColor = 'white'; // default
      if (data.beltConfig) {
        // Original Marune app stores belt info in beltConfig object
        if (data.beltConfig.baseColor) {
          beltColor = data.beltConfig.baseColor;
        } else if (data.beltConfig.designation) {
          // Original app may use 'designation' instead of 'baseColor'
          beltColor = data.beltConfig.designation;
        } else if (data.beltConfig.name) {
          // Extract color from name (e.g., "Blue Belt" -> "blue")
          const colorMatch = data.beltConfig.name.toLowerCase().match(/(white|blue|purple|brown|black|yellow|orange|green|red|coral)/);
          if (colorMatch) {
            beltColor = colorMatch[1];
          }
        }
      } else if (data.beltColor) {
        // Fallback to direct beltColor field if it exists
        beltColor = data.beltColor;
      } else if (data.designation) {
        // Legacy fallback: direct designation field
        beltColor = data.designation;
      }

      // Spread raw data first, then override with our carefully extracted values
      // so our extraction always wins over raw data fields
      return {
        ...data, // Include any other fields from the data
        id,
        userId,
        date: data.date || 0,
        beltColor,
        stripes: stripeCount,
        martialArt,
        academy: data.academy,
        instructor: data.instructor,
        notes: data.notes || data.note,
        photoURL: data.photoURL || data.photo?.url,
        isActive: data.isActive !== false, // Default to true if not specified
        beltConfig: data.beltConfig, // Keep the original beltConfig for reference
      };
    });
    
    // Repair pass: migrate any legacy profile promotionHistory entries that
    // never made it into /beltPromotions/. Earlier migrations copied only the
    // current belt, collapsing a legacy user's history to one record — this
    // restores the rest. Dedupe makes it a no-op after the first pass.
    const repairedFromHistory = await migrateProfilePromotionHistory(userId, userProfile, promotions);
    promotions.push(...repairedFromHistory);

    // If we have promotions from Firebase but no BJJ promotion, migrate profile BJJ belt data
    const hasBJJPromotion = promotions.some(p =>
      p.martialArt === 'Brazilian Jiu Jitsu' ||
      p.martialArt?.toLowerCase().includes('jiu jitsu') ||
      p.martialArt?.toLowerCase() === 'bjj'
    );

    if (hasBJJBeltInfo && !hasBJJPromotion) {
      const bjjBelt = userProfile!.belt!["Brazilian Jiu Jitsu"];

      // Parse the stripe count - check stripeCount (new) then stripes (legacy),
      // mirroring the primary migration path above
      let stripeCount = 0;
      if (bjjBelt.stripeCount !== undefined) {
        stripeCount = typeof bjjBelt.stripeCount === 'number' ? bjjBelt.stripeCount : parseInt(bjjBelt.stripeCount, 10) || 0;
      } else if (bjjBelt.stripes !== undefined) {
        if (typeof bjjBelt.stripes === 'number') {
          stripeCount = bjjBelt.stripes;
        } else if (typeof bjjBelt.stripes === 'string') {
          stripeCount = parseInt(bjjBelt.stripes, 10);
          if (isNaN(stripeCount)) stripeCount = 0;
        }
      } else if (userProfile?.stripesCount !== undefined) {
        if (typeof userProfile.stripesCount === 'number') {
          stripeCount = userProfile.stripesCount;
        } else if (typeof userProfile.stripesCount === 'string') {
          stripeCount = parseInt(userProfile.stripesCount, 10);
          if (isNaN(stripeCount)) stripeCount = 0;
        }
      }

      // Extract belt color robustly - check all possible field locations
      let beltColor = 'white';
      if (bjjBelt.baseColor) {
        beltColor = bjjBelt.baseColor;
      } else if (bjjBelt.designation) {
        const colorMatch = bjjBelt.designation.toLowerCase().match(/(white|blue|purple|brown|black|yellow|orange|green|red|coral)/);
        beltColor = colorMatch ? colorMatch[1] : bjjBelt.designation;
      } else if (bjjBelt.name) {
        const colorMatch = bjjBelt.name.toLowerCase().match(/(white|blue|purple|brown|black|yellow|orange|green|red|coral)/);
        beltColor = colorMatch ? colorMatch[1] : bjjBelt.name;
      } else if (bjjBelt.color) {
        beltColor = bjjBelt.color;
      }
      logger.info('[BeltProgressionService] Extracted belt color for secondary migration:', beltColor);

      // Migrate profile BJJ belt data to /beltPromotions/ - create a REAL promotion
      logger.info('[BeltProgressionService] Migrating missing BJJ belt from profile to /beltPromotions/');
      try {
        const migratedPromotion = await addBeltPromotion({
          userId,
          date: bjjBelt.dateObtained || Date.now(),
          beltColor,
          stripes: stripeCount,
          martialArt: 'Brazilian Jiu Jitsu',
          academy: userProfile.school?.name,
          instructor: userProfile.school?.instructor,
          isActive: true,
          // Migration of existing data, not a new promotion — keep it off feeds
          shownOnFeeds: false
        });
        promotions.push(migratedPromotion);
        logger.info('[BeltProgressionService] Successfully migrated BJJ belt, new promotion ID:', migratedPromotion.id);
      } catch (migrationError) {
        logger.error('[BeltProgressionService] Failed to migrate BJJ belt data:', migrationError);
        // Don't add synthetic data, just continue with existing promotions
      }
    }
    
    logger.info('[BeltProgressionService] Final promotions being returned:', promotions);
    return promotions;
  } catch (error) {
    logger.error('Error fetching belt promotions:', error);
    throw error;
  }
};
/**
 * Add a new belt promotion
 * @param promotion Belt promotion data to add
 * @returns The added promotion with ID
 */
export const addBeltPromotion = async (promotion: Omit<BeltPromotion, 'id'>): Promise<BeltPromotion> => {
  try {
    const { userId } = promotion;

    // Transform Phoenix structure to match original app structure EXACTLY
    const date = promotion.date || Date.now();
    const promotionDate = new Date(date);
    const timezoneOffset = promotionDate.getTimezoneOffset() / 60;

    // Create title and beltConfig.name like original app (e.g., "Promoted to Purple Belt")
    const beltColorCapitalized = promotion.beltColor.charAt(0).toUpperCase() + promotion.beltColor.slice(1);
    const beltName = `${beltColorCapitalized} Belt`;
    const title = `Promoted to ${beltName}`;

    // Use provided beltConfig if available (from RankSelector or CustomBeltConfigurator)
    // Otherwise construct a default one
    const providedBeltConfig = (promotion as any).beltConfig;
    // NOTE: Firebase rules use 'stipeCount' (original app typo, missing 'r').
    // We must match this field name exactly or writes are rejected with PERMISSION_DENIED.
    const beltConfig = providedBeltConfig ? {
      name: providedBeltConfig.name || beltName,
      baseColor: providedBeltConfig.baseColor || promotion.beltColor,
      rank: providedBeltConfig.rank || beltName,
      level: providedBeltConfig.level || 'adult',
      stipeCount: providedBeltConfig.stripeCount ?? promotion.stripes ?? 0,
      contrastColor: providedBeltConfig.contrastColor || '#FFFFFF',
      contrastType: providedBeltConfig.contrastType || 'solid',
      stripeColor: providedBeltConfig.stripeColor || '#FFFFFF',
      stripeBgColor: providedBeltConfig.stripeBgColor || promotion.beltColor,
    } : {
      name: beltName, // REQUIRED by Firebase rules - e.g., "Purple Belt"
      baseColor: promotion.beltColor,
      rank: beltName, // Original app includes this
      level: 'adult', // Original app validates this field
      stipeCount: promotion.stripes || 0,
      contrastColor: '#FFFFFF', // Default values matching original app belt configs
      contrastType: 'solid',
      stripeColor: '#FFFFFF',
      stripeBgColor: promotion.beltColor,
    };

    // D4: Extract location from promotion data (supports both direct property and nested)
    const promotionLocation = (promotion as any).location ?? null;

    const promotionData = {
      date: promotionDate.getTime(),
      timezoneOffset,
      title,
      martialArtName: promotion.martialArt, // Original app uses martialArtName
      beltConfig,
      // D4: Use provided location or null (original app sends null or location object)
      location: promotionLocation ? {
        latitude: promotionLocation.latitude,
        longitude: promotionLocation.longitude,
        placeID: promotionLocation.placeID,
        locality: promotionLocation.locality
      } : null,
      instructor: promotion.instructor || '',
      note: promotion.notes || '', // Original app uses 'note' not 'notes'
      taggedFriends: {}, // Original app sends empty object
      // Original app default is true; migrations pass false so historical
      // records don't fan out to friends' feeds as new promotions
      shownOnFeeds: promotion.shownOnFeeds ?? true,
      dateCreated: Date.now(), // Original app adds this for new promotions
    };

    // Use Firebase create (push) to auto-generate key (matching original app)
    const ref = await create(`beltPromotions/${userId}`, promotionData);
    const promotionId = ref.key!;

    // NOTE: Original Marune app does NOT automatically update user profile when creating
    // a belt promotion. Belt promotions are for feed/social features only.
    // Profile belt data is edited separately via EditProfile screen.
    // This matches the original app's architecture.

    // Notification is handled by the 'beltPromotionsChanged' Cloud Function
    // which triggers on /beltPromotions/{uid}/{key} writes

    // Return the promotion with ID
    return {
      ...promotion,
      id: promotionId
    };
  } catch (error) {
    logger.error('Error adding belt promotion:', error);
    throw error;
  }
};
/**
 * Update an existing belt promotion
 * @param id Promotion ID to update
 * @param data Updated data for the promotion
 * @returns The updated promotion
 */
export const updateBeltPromotion = async (id: string, data: Partial<BeltPromotion>): Promise<BeltPromotion> => {
  try {
    if (!data.userId) {
      throw new Error('User ID is required for updating a belt promotion');
    }

    const { userId } = data;

    // Get the current promotion data
    const currentPromotion = await getData(`/beltPromotions/${userId}/${id}`);
    if (!currentPromotion) {
      throw new Error(`Belt promotion ${id} not found for user ${userId}`);
    }

    // Transform Phoenix format to Firebase/original app format
    // This ensures field names match what Firebase expects
    const date = data.date || currentPromotion.date;
    const promotionDate = new Date(date);
    const timezoneOffset = promotionDate.getTimezoneOffset() / 60;

    // Build beltConfig matching original app format
    const beltColor = data.beltColor || currentPromotion.beltConfig?.baseColor || 'white';
    const beltColorCapitalized = beltColor.charAt(0).toUpperCase() + beltColor.slice(1);
    const beltName = `${beltColorCapitalized} Belt`;

    // Use provided beltConfig or build from data
    const providedBeltConfig = (data as any).beltConfig;
    const stripeCount = providedBeltConfig?.stripeCount ?? data.stripes ?? currentPromotion.beltConfig?.stripeCount ?? currentPromotion.beltConfig?.stipeCount ?? 0;

    // NOTE: Firebase rules use 'stipeCount' (original app typo, missing 'r').
    const beltConfig = providedBeltConfig ? {
      name: providedBeltConfig.name || beltName,
      baseColor: providedBeltConfig.baseColor || beltColor,
      rank: providedBeltConfig.rank || beltName,
      level: providedBeltConfig.level || currentPromotion.beltConfig?.level || 'adult',
      stipeCount: stripeCount,
      contrastColor: providedBeltConfig.contrastColor || currentPromotion.beltConfig?.contrastColor || '#FFFFFF',
      contrastType: providedBeltConfig.contrastType || currentPromotion.beltConfig?.contrastType || 'solid',
      stripeColor: providedBeltConfig.stripeColor || currentPromotion.beltConfig?.stripeColor || '#FFFFFF',
      stripeBgColor: providedBeltConfig.stripeBgColor || currentPromotion.beltConfig?.stripeBgColor || beltColor,
    } : {
      name: beltName,
      baseColor: beltColor,
      rank: beltName,
      level: currentPromotion.beltConfig?.level || 'adult',
      stipeCount: stripeCount,
      contrastColor: currentPromotion.beltConfig?.contrastColor || '#FFFFFF',
      contrastType: currentPromotion.beltConfig?.contrastType || 'solid',
      stripeColor: currentPromotion.beltConfig?.stripeColor || '#FFFFFF',
      stripeBgColor: currentPromotion.beltConfig?.stripeBgColor || beltColor,
    };

    // D4: Handle location - accept new location from data, or preserve existing
    const dataLocation = (data as any).location;
    let updatedLocation = currentPromotion.location || null;

    // If location is explicitly provided (even as null), use it
    if (dataLocation !== undefined) {
      updatedLocation = dataLocation ? {
        latitude: dataLocation.latitude,
        longitude: dataLocation.longitude,
        placeID: dataLocation.placeID,
        locality: dataLocation.locality
      } : null;
    }

    // Build the Firebase-compatible update object
    // Use original app field names: martialArtName (not martialArt), note (not notes)
    const firebaseData: Record<string, any> = {
      date: promotionDate.getTime(),
      timezoneOffset,
      title: currentPromotion.title || `Promoted to ${beltName}`,
      martialArtName: data.martialArt || currentPromotion.martialArtName,
      beltConfig,
      // D4: Use updated location (from data or preserved from existing)
      location: updatedLocation,
      instructor: data.instructor ?? currentPromotion.instructor ?? '',
      note: data.notes ?? currentPromotion.note ?? '', // Original app uses 'note'
      taggedFriends: currentPromotion.taggedFriends || {},
      shownOnFeeds: currentPromotion.shownOnFeeds ?? true,
    };

    // Only include photo if it exists
    if (data.photoURL || currentPromotion.photo) {
      firebaseData.photo = data.photoURL
        ? { url: data.photoURL, aspectRatio: 1 }
        : currentPromotion.photo;
    }

    // Only include academy if provided
    if (data.academy || currentPromotion.academy) {
      firebaseData.academy = data.academy || currentPromotion.academy;
    }

    // Update the promotion with normalized data
    await updateData(`/beltPromotions/${userId}/${id}`, firebaseData);
    
    // Check if this was the most recent promotion for the martial art
    // If so, update the user's belt status
    // Note: Firebase stores as 'martialArtName' but BeltPromotion type uses 'martialArt'
    const artNameForUpdate = data.martialArt || (currentPromotion as any).martialArtName || currentPromotion.martialArt;
    const allPromotions = await getUserBeltPromotions(userId);
    const artPromotions = allPromotions.filter(p =>
      p.martialArt === artNameForUpdate || (p as any).martialArtName === artNameForUpdate
    );
    const sortedPromotions = artPromotions.sort((a, b) => b.date - a.date);

    if (sortedPromotions.length > 0 && sortedPromotions[0].id === id) {
      // This is the most recent promotion, update user's belt status
      await updateUserBeltStatus(userId, artNameForUpdate, {
        beltColor: beltColor,
        stripes: stripeCount,
        instructor: data.instructor || currentPromotion.instructor,
        academy: data.academy || currentPromotion.academy,
        date: promotionDate.getTime()
      });
    }

    // Return the updated promotion with normalized data
    return {
      ...currentPromotion,
      id,
      userId,
      date: promotionDate.getTime(),
      beltColor: beltColor,
      stripes: stripeCount,
      martialArt: artNameForUpdate,
      instructor: data.instructor ?? currentPromotion.instructor ?? '',
      academy: data.academy || currentPromotion.academy,
      notes: data.notes ?? (currentPromotion as any).note ?? '',
      photoURL: data.photoURL || currentPromotion.photo?.url,
      isActive: true,
      beltConfig: beltConfig,
      // D4: Include updated location in returned promotion
      location: updatedLocation
    } as BeltPromotion;
  } catch (error) {
    logger.error('Error updating belt promotion:', error);
    throw error;
  }
};
/**
 * Delete a belt promotion
 * @param id Promotion ID to delete
 * @param userId User ID the promotion belongs to
 * @returns True if deletion was successful
 */
export const deleteBeltPromotion = async (id: string, userId: string): Promise<boolean> => {
  try {
    // Get the current promotion data before deleting
    const currentPromotion = await getData(`/beltPromotions/${userId}/${id}`);
    if (!currentPromotion) {
      throw new Error(`Belt promotion ${id} not found for user ${userId}`);
    }
    
    // Delete the promotion
    await removeData(`/beltPromotions/${userId}/${id}`);

    // Get martial art name - Firebase stores as martialArtName, our type uses martialArt
    const martialArtName = currentPromotion.martialArtName || currentPromotion.martialArt || 'Brazilian Jiu Jitsu';

    // Check if this was the most recent promotion for the martial art
    // If so, update the user's belt status to the previous promotion
    const allPromotions = await getUserBeltPromotions(userId);
    const artPromotions = allPromotions.filter(p =>
      p.martialArt === martialArtName ||
      (p as any).martialArtName === martialArtName
    );
    const sortedPromotions = artPromotions.sort((a, b) => b.date - a.date);

    if (sortedPromotions.length > 0) {
      // There's still at least one promotion, update to the most recent
      const mostRecent = sortedPromotions[0];
      await updateUserBeltStatus(userId, martialArtName, {
        beltColor: mostRecent.beltColor,
        stripes: mostRecent.stripes,
        instructor: mostRecent.instructor,
        academy: mostRecent.academy,
        date: mostRecent.date
      });
    } else {
      // No promotions left, remove the belt status for this martial art
      // This should be handled carefully to avoid data loss
      // For now, just set to white belt with 0 stripes
      await updateUserBeltStatus(userId, martialArtName, {
        beltColor: 'white',
        stripes: 0,
        date: Date.now()
      });
    }
    
    return true;
  } catch (error) {
    logger.error('Error deleting belt promotion:', error);
    throw error;
  }
};
/**
 * Subscribe to belt promotions for a user
 * @param userId User ID to subscribe to
 * @param callback Function to call when promotions change
 * @returns Unsubscribe function
 */
export const subscribeToBeltPromotions = (
  userId: string, 
  callback: (promotions: BeltPromotion[]) => void
): (() => void) => {
  const path = `/beltPromotions/${userId}`;
  
  const unsubscribe = subscribeToData(path, (data) => {
    if (!data) {
      callback([]);
      return;
    }
    
    // Convert object to array and add IDs
    const promotions: BeltPromotion[] = Object.entries(data).map(([id, promotionData]: [string, any]) => ({
      id,
      userId,
      ...promotionData
    }));
    
    callback(promotions);
  });
  
  return unsubscribe;
};
/**
 * Get belt promotions for a specific martial art
 * @param userId User ID to get promotions for
 * @param martialArt Martial art to filter by
 * @returns Array of belt promotions for the martial art
 */
export const getBeltPromotionsByMartialArt = async (userId: string, martialArt: string): Promise<BeltPromotion[]> => {
  try {
    const allPromotions = await getUserBeltPromotions(userId);
    return allPromotions.filter(promotion => promotion.martialArt === martialArt);
  } catch (error) {
    logger.error('Error fetching belt promotions by martial art:', error);
    throw error;
  }
};
/**
 * Get a user's current belt for a specific martial art
 * @param userId User ID to get belt for
 * @param martialArt Martial art to get belt for
 * @returns The current belt or null if not found
 */
export const getCurrentBeltForMartialArt = async (userId: string, martialArt: string): Promise<BeltPromotion | null> => {
  try {
    const promotions = await getBeltPromotionsByMartialArt(userId, martialArt);
    if (promotions.length === 0) {
      return null;
    }
    
    // Sort by date (newest first) and return the first one
    const sortedPromotions = [...promotions].sort((a, b) => b.date - a.date);
    return sortedPromotions[0];
  } catch (error) {
    logger.error('Error getting current belt:', error);
    throw error;
  }
};
/**
 * Helper function to update a user's belt status in their profile
 * @param userId User ID to update
 * @param martialArt Martial art to update belt for
 * @param beltData Belt data to update
 */
const updateUserBeltStatus = async (
  userId: string,
  martialArt: string,
  beltData: {
    beltColor: string;
    stripes: number;
    instructor?: string;
    academy?: string;
    date?: number;
  }
): Promise<void> => {
  try {
    // D1: Normalize martial art name (handles empty, BJJ variations, etc.)
    const normalizedMartialArt = normalizeMartialArtSafe(martialArt, 'Brazilian Jiu Jitsu');

    // D2: Ensure stripes is a valid number
    const stripeCount = parseStripeCount(beltData.stripes, 0);

    // D8: Handle optional fields safely
    const instructorName = getInstructor(beltData.instructor);
    const academyName = getAcademy(beltData.academy);

    // First get the existing profile
    const profileData = await getData(`/users/${userId}`);
    if (!profileData) {
      throw new Error(`User profile for ${userId} not found`);
    }

    // Update both new and legacy data structures for compatibility

    // 1. Update the martialArts field (new structure)
    if (!profileData.martialArts) {
      profileData.martialArts = {};
    }

    // D6: Use deterministic martial art ID to prevent duplicates
    const martialArtId = findOrCreateMartialArtId(profileData.martialArts, normalizedMartialArt);

    const existingMartialArt = profileData.martialArts[martialArtId] || {};

    profileData.martialArts[martialArtId] = {
      ...existingMartialArt,
      name: normalizedMartialArt,
      rank: beltData.beltColor,
      stripes: stripeCount,
      instructor: instructorName || existingMartialArt.instructor,
      startDate: existingMartialArt.startDate || Date.now(),
      experience: existingMartialArt.experience || (Date.now() - (existingMartialArt.startDate || Date.now()))
    };

    // 2. Update the legacy belt field for compatibility
    if (!profileData.belt) {
      profileData.belt = {};
    }

    // Check if this is a BJJ martial art (using normalized name)
    const isBJJ = normalizedMartialArt === 'Brazilian Jiu Jitsu';

    // If this is BJJ, update the BJJ-specific field
    if (isBJJ) {
      if (!profileData.belt['Brazilian Jiu Jitsu']) {
        profileData.belt['Brazilian Jiu Jitsu'] = {};
      }

      profileData.belt['Brazilian Jiu Jitsu'] = {
        ...profileData.belt['Brazilian Jiu Jitsu'],
        designation: beltData.beltColor,
        name: beltData.beltColor,
        stripes: stripeCount,
        dateObtained: beltData.date || Date.now()
      };

      // Also update the general belt fields for compatibility
      profileData.belt = {
        ...profileData.belt,
        designation: beltData.beltColor,
        beltDesignation: beltData.beltColor,
        name: beltData.beltColor,
        stripes: stripeCount,
        dateObtained: beltData.date || Date.now(),
        promotedOn: beltData.date || Date.now(),
        instructor: instructorName || profileData.belt.instructor
      };

      // Update stripesCount for compatibility
      profileData.stripesCount = stripeCount;
    }

    // This is another martial art, update in the legacy format as a field of the belt object
    if (!profileData.belt[normalizedMartialArt]) {
      profileData.belt[normalizedMartialArt] = {};
    }

    profileData.belt[normalizedMartialArt] = {
      designation: beltData.beltColor,
      name: beltData.beltColor,
      stripes: stripeCount,
      dateObtained: beltData.date || Date.now()
    };

    // 3. Update the promotion history in the user's profile
    if (!profileData.belt.promotionHistory) {
      profileData.belt.promotionHistory = [];
    }

    // Add to promotion history if it doesn't already exist
    const promotionExists = profileData.belt.promotionHistory.some(
      (p: any) => p.date === beltData.date && p.belt === beltData.beltColor
    );

    if (!promotionExists && beltData.date) {
      // Create the promotion history record
      const promotionRecord = {
        id: `hist-${Date.now()}`,
        belt: beltData.beltColor,
        stripes: stripeCount,
        date: beltData.date,
        academy: academyName,
        instructor: instructorName
      };

      logger.info('[updateUserBeltStatus] Adding promotion record:', promotionRecord);
      profileData.belt.promotionHistory.push(promotionRecord);

      // Sort by date (oldest first)
      profileData.belt.promotionHistory.sort((a: any, b: any) => a.date - b.date);
    }

    // Update the user profile
    await updateData(`/users/${userId}`, {
      martialArts: profileData.martialArts,
      belt: profileData.belt,
      stripesCount: profileData.stripesCount
    });

    logger.info(`[updateUserBeltStatus] Updated belt status for ${normalizedMartialArt}: ${beltData.beltColor} ${stripeCount} stripes`);
  } catch (error) {
    logger.error('[updateUserBeltStatus] Error:', error);
    throw error;
  }
};
/**
 * Get all belt promotion history for a user's martial arts
 * @param userId User ID to get history for
 * @returns Array of belt promotions sorted by date
 */
export const getBeltPromotionHistory = async (userId: string): Promise<Array<{
  id: string;
  belt: string;
  stripes: number;
  date: number;
  academy?: string;
  instructor?: string;
  martialArt: string;
  notes?: string;
  photoUrl?: string;
}>> => {
  try {
    // First check if we have actual belt promotions - if so, use those instead of synthetic data
    const actualPromotions = await getUserBeltPromotions(userId);
    if (actualPromotions && actualPromotions.length > 0) {
      // Using actual promotions instead of synthetic history
      
      // Convert promotions to history format
      return actualPromotions.map(promotion => ({
        id: promotion.id,
        belt: promotion.beltColor,
        stripes: promotion.stripes,
        date: promotion.date,
        academy: promotion.academy,
        instructor: promotion.instructor,
        martialArt: promotion.martialArt,
        notes: promotion.notes,
        photoUrl: promotion.photoURL
      }));
    }
    
    // Get the user profile
    const profileData = await getData(`/users/${userId}`);
    if (!profileData) {
      return [];
    }
    
    // Check if we have promotion history in the belt object - migrate to /beltPromotions/
    if (profileData.belt && profileData.belt.promotionHistory &&
        Array.isArray(profileData.belt.promotionHistory) &&
        profileData.belt.promotionHistory.length > 0) {

      logger.info('[BeltProgressionService] Found promotionHistory in profile, migrating to /beltPromotions/');
      const migratedPromotions: any[] = [];

      for (const item of profileData.belt.promotionHistory) {
        try {
          const beltColor = item.belt || profileData.belt.designation || 'white';
          const stripeCount = typeof item.stripes !== 'undefined' ? item.stripes :
            (profileData.stripesCount !== undefined ? profileData.stripesCount : 0);

          const migratedPromotion = await addBeltPromotion({
            userId,
            date: item.date || Date.now(),
            beltColor,
            stripes: stripeCount,
            martialArt: item.martialArt || 'Brazilian Jiu Jitsu',
            academy: item.academy,
            instructor: item.instructor,
            notes: item.notes,
            isActive: true,
            // Migration of existing data, not a new promotion — keep it off feeds
            shownOnFeeds: false
          });

          migratedPromotions.push({
            id: migratedPromotion.id,
            belt: beltColor,
            stripes: stripeCount,
            date: item.date || Date.now(),
            academy: item.academy,
            instructor: item.instructor,
            martialArt: item.martialArt || 'Brazilian Jiu Jitsu',
            notes: item.notes,
            photoUrl: item.photoUrl
          });
        } catch (migrationError) {
          logger.error('[BeltProgressionService] Failed to migrate history item:', migrationError);
        }
      }

      if (migratedPromotions.length > 0) {
        return migratedPromotions;
      }
    }

    // No explicit history found - return empty array (no synthetic data)
    // The profile belt data should have been migrated by getUserBeltPromotions already
    return [];
  } catch (error) {
    logger.error('Error getting belt promotion history:', error);
    throw error;
  }
};
/**
 * Get all martial arts a user has trained in
 * @param userId User ID to get martial arts for
 * @returns Array of martial arts with belt info
 */
export const getUserMartialArts = async (userId: string): Promise<Array<{
  id: string;
  name: string;
  rank: string;
  stripes: number;
  startDate: number;
  experience: number;
  instructor?: string;
}>> => {
  try {
    // Get the user profile
    const profileData = await getData(`/users/${userId}`);
    if (!profileData || !profileData.martialArts) {
      return [];
    }
    
    // Convert object to array and add IDs
    return Object.entries(profileData.martialArts).map(([id, data]: [string, any]) => ({
      id,
      name: data.name,
      rank: data.rank || 'white',
      stripes: data.stripes || 0,
      startDate: data.startDate || 0,
      experience: data.experience || 0,
      instructor: data.instructor
    }));
  } catch (error) {
    logger.error('Error getting user martial arts:', error);
    throw error;
  }
};
const exportedObject = {
  saveBeltPromotion,
  getUserBeltPromotions,
  addBeltPromotion,
  updateBeltPromotion,
  deleteBeltPromotion,
  subscribeToBeltPromotions,
  getBeltPromotionsByMartialArt,
  getCurrentBeltForMartialArt,
  getBeltPromotionHistory,
  getUserMartialArts
};
export default exportedObject;